export type StaffRole = 'OWNER' | 'MANAGER' | 'CASHIER' | 'KITCHEN' | 'OPERATOR'

export interface Staff {
  id: string
  storeId: string
  username: string
  displayName: string
  role: StaffRole
}

export type OrderStatus = 'PENDING_PAYMENT' | 'PAID' | 'PREPARING' | 'READY' | 'COMPLETED' | 'CANCELLED'

export interface AdminOrder {
  id: string
  orderNumber: string
  storeId: string
  platform: 'WECHAT' | 'DOUYIN' | 'ALIPAY'
  fulfillmentType: 'DINE_IN' | 'PICKUP'
  status: OrderStatus
  tableNumber?: string | null
  pickupNumber?: string | null
  remark?: string | null
  payableFen: number
  version: number
  createdAt: string
  lines: Array<{
    menuItemId: string
    name: string
    quantity: number
    lineTotalFen: number
  }>
}

export interface AdminMenuItem {
  id: string
  name: string
  categoryName: string
  priceFen: number
  available: boolean
  published: boolean
  sortOrder: number
}

interface LoginResponse {
  staff: Staff
  csrfToken: string
  expiresAt: string
}

interface ErrorResponse {
  code?: string
  message?: string
}

export class AdminApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string) {
    super(message)
    this.name = 'AdminApiError'
  }
}

const CSRF_STORAGE_KEY = 'wokgo.admin.csrf'

async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as ErrorResponse
    throw new AdminApiError(
      response.status,
      body.code ?? 'ADMIN_REQUEST_FAILED',
      body.message ?? '请求失败，请稍后重试',
    )
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export function hasCsrfToken(): boolean {
  return Boolean(sessionStorage.getItem(CSRF_STORAGE_KEY))
}

export async function login(input: { storeId: string; username: string; password: string }): Promise<Staff> {
  const response = await adminFetch<LoginResponse>('/v1/admin/auth/login', {
    method: 'POST',
    body: JSON.stringify(input),
  })
  sessionStorage.setItem(CSRF_STORAGE_KEY, response.csrfToken)
  return response.staff
}

export function getMe(): Promise<Staff> {
  return adminFetch('/v1/admin/me')
}

export async function logout(): Promise<void> {
  const csrfToken = sessionStorage.getItem(CSRF_STORAGE_KEY)
  try {
    await adminFetch('/v1/admin/auth/logout', {
      method: 'POST',
      headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : undefined,
    })
  } finally {
    sessionStorage.removeItem(CSRF_STORAGE_KEY)
  }
}

export function listActiveOrders(storeId: string): Promise<AdminOrder[]> {
  return adminFetch(`/v1/admin/stores/${storeId}/orders`)
}

export function transitionOrder(order: AdminOrder, target: OrderStatus): Promise<AdminOrder> {
  const csrfToken = sessionStorage.getItem(CSRF_STORAGE_KEY)
  if (!csrfToken) {
    return Promise.reject(new AdminApiError(403, 'CSRF_MISSING', '登录状态已失效，请重新登录'))
  }
  return adminFetch(`/v1/admin/orders/${order.id}/status`, {
    method: 'PATCH',
    headers: { 'X-CSRF-Token': csrfToken },
    body: JSON.stringify({ expectedVersion: order.version, target }),
  })
}

export function listAdminMenu(storeId: string): Promise<AdminMenuItem[]> {
  return adminFetch(`/v1/admin/stores/${storeId}/menu`)
}

export function setMenuAvailability(
  storeId: string,
  item: AdminMenuItem,
  available: boolean,
): Promise<void> {
  const csrfToken = sessionStorage.getItem(CSRF_STORAGE_KEY)
  if (!csrfToken) {
    return Promise.reject(new AdminApiError(403, 'CSRF_MISSING', '登录状态已失效，请重新登录'))
  }
  return adminFetch(`/v1/admin/menu/items/${item.id}/availability`, {
    method: 'PATCH',
    headers: { 'X-CSRF-Token': csrfToken },
    body: JSON.stringify({ storeId, available }),
  })
}
